import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

import { ApiClientError } from "@chaochud/api-client";
import type { AdminLoginInput } from "@chaochud/validation";
import { adminLoginSchema } from "@chaochud/validation";

import { Logo } from "@/components/logo";
import { useAdminAuth } from "@/lib/auth/auth-context";

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case "INVALID_CREDENTIALS":
        return "อีเมลหรือรหัสผ่านไม่ถูกต้อง";
      case "ACCESS_DENIED":
        return "บัญชีนี้ไม่มีสิทธิ์ผู้ดูแลระบบ";
      case "ACCOUNT_SUSPENDED":
        return "บัญชีนี้ถูกระงับการใช้งาน";
      case "EMAIL_NOT_VERIFIED":
        return "กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ";
      case "RATE_LIMIT_EXCEEDED":
        return "มีการเรียกใช้งานบ่อยเกินไป กรุณาลองใหม่ในอีกสักครู่";
      default:
        return error.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
    }
  }
  return "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isHydrated } = useAdminAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AdminLoginInput>({ resolver: zodResolver(adminLoginSchema) });

  if (isHydrated && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-bg p-6">
      <Logo iconSize={32} textClassName="text-2xl" />
      <div className="w-full max-w-sm rounded-xl border border-slate-line bg-slate-surface p-6 shadow-sm">
        <h2 className="mb-4 font-heading text-lg font-semibold text-ink">เข้าสู่ระบบผู้ดูแล</h2>
        <form
          className="flex flex-col gap-3"
          onSubmit={handleSubmit(async (values) => {
            setServerError(null);
            try {
              const result = await login(values);
              if (result.status === "AUTHENTICATED") {
                navigate("/dashboard");
                return;
              }
              if (result.status === "MFA_REQUIRED") {
                navigate("/mfa/verify", { state: { mfaToken: result.mfaToken } });
                return;
              }
              navigate("/mfa/setup", { state: { mfaToken: result.mfaToken } });
            } catch (error) {
              setServerError(toErrorMessage(error));
            }
          })}
        >
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-sm font-medium text-ink">
              อีเมล
            </label>
            <input
              id="email"
              type="email"
              className="rounded-lg border border-slate-line px-3 py-2 text-sm"
              {...register("email")}
            />
            {errors.email ? <p className="text-xs text-error-text">{errors.email.message}</p> : null}
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium text-ink">
              รหัสผ่าน
            </label>
            <input
              id="password"
              type="password"
              className="rounded-lg border border-slate-line px-3 py-2 text-sm"
              {...register("password")}
            />
            {errors.password ? <p className="text-xs text-error-text">{errors.password.message}</p> : null}
          </div>
          {serverError ? <p className="text-sm text-error-text">{serverError}</p> : null}
          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-2 rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            เข้าสู่ระบบ
          </button>
        </form>
      </div>
    </main>
  );
}
